'use client';

import { Box, Button, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { FilterSectionHeading } from '@odys/design-system/components/FilterSectionHeading';
import { SearchField } from '@odys/design-system/components/SearchField';
import { useMemo, useState } from 'react';

/** Nombre de cases affichées en mode replié, et seuil qui bascule en mode étendu. */
const COLLAPSED_VISIBLE_COUNT = 5;

export type FilterOption = { value: string; label: string };

type FilterCheckboxGroupLabels = {
    searchPlaceholder: string;
    searchEmpty: string;
    seeAll: string;
    seeLess: string;
};

type FilterCheckboxGroupProps = {
    title: string;
    options: FilterOption[];
    selected: string[];
    onToggle: (value: string) => void;
    /** Préfixe des `data-testid` : `${testIdPrefix}` (conteneur), `-search`, `-see-all`, `-${value}`. */
    testIdPrefix: string;
    labels: FilterCheckboxGroupLabels;
};

/**
 * Groupe de filtres à cases à cocher au format Figma :
 * - ≤ 5 options → toutes les cases affichées, sans recherche ni « voir plus ».
 * - > 5 options → barre de recherche + 5 cases + bouton « voir plus / voir moins ».
 *
 * La règle est dérivée du seul nombre d'options : les appelants n'ont rien à
 * configurer, ce qui garde tous les filtres des listes catalogue cohérents.
 */
export function FilterCheckboxGroup({
    title,
    options,
    selected,
    onToggle,
    testIdPrefix,
    labels,
}: FilterCheckboxGroupProps) {
    const [query, setQuery] = useState('');
    const [expanded, setExpanded] = useState(false);

    const isExpandable = options.length > COLLAPSED_VISIBLE_COUNT;

    const filtered = useMemo(() => {
        if (!isExpandable) return options;
        const needle = query.trim().toLocaleLowerCase();
        if (needle.length === 0) return options;
        return options.filter((o) => o.label.toLocaleLowerCase().includes(needle));
    }, [options, query, isExpandable]);

    const showSeeAll = isExpandable && filtered.length > COLLAPSED_VISIBLE_COUNT;
    const visible = showSeeAll && !expanded ? filtered.slice(0, COLLAPSED_VISIBLE_COUNT) : filtered;

    if (options.length === 0) return null;

    return (
        <Box data-testid={testIdPrefix}>
            <FilterSectionHeading>{title}</FilterSectionHeading>
            {isExpandable && (
                <SearchField
                    value={query}
                    onChange={setQuery}
                    placeholder={labels.searchPlaceholder}
                    fullWidth
                    sx={{ mt: 1, mb: 0.5 }}
                    data-testid={`${testIdPrefix}-search`}
                />
            )}
            {filtered.length === 0 ? (
                <Typography
                    variant="body2"
                    data-testid={`${testIdPrefix}-empty`}
                    sx={{
                        color: 'text.secondary',
                        mt: 1,
                        pl: 0.5,
                    }}
                >
                    {labels.searchEmpty}
                </Typography>
            ) : (
                <FormGroup sx={{ pl: 0.5 }}>
                    {visible.map((option) => (
                        <FormControlLabel
                            key={option.value}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={selected.includes(option.value)}
                                    onChange={() => onToggle(option.value)}
                                    data-testid={`${testIdPrefix}-${option.value}`}
                                />
                            }
                            label={<Typography variant="body2">{option.label}</Typography>}
                        />
                    ))}
                </FormGroup>
            )}
            {showSeeAll && (
                <Button
                    variant="text"
                    size="small"
                    onClick={() => setExpanded((v) => !v)}
                    data-testid={`${testIdPrefix}-see-all`}
                    sx={{ alignSelf: 'flex-start', px: 0.5 }}
                >
                    {expanded ? labels.seeLess : labels.seeAll}
                </Button>
            )}
        </Box>
    );
}
